import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { UserDto } from '@jordan/types';
import { apiGet } from '../lib/api';

// undefined = loading, null = failed/none, UserDto = loaded
interface MeContextValue {
  me: UserDto | null | undefined;
  refresh: () => Promise<void>;
}

const MeContext = createContext<MeContextValue | undefined>(undefined);

/** Fetches the authenticated user (incl. role) for the protected app, with a refresh. */
export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<UserDto | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      setMe(await apiGet<UserDto>('/api/me'));
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <MeContext.Provider value={{ me, refresh }}>{children}</MeContext.Provider>;
}

export function useMe(): UserDto | null | undefined {
  return useContext(MeContext)?.me;
}

/** Re-fetch the authenticated user (e.g. after editing the profile or avatar). */
export function useRefreshMe(): () => Promise<void> {
  const ctx = useContext(MeContext);
  return ctx?.refresh ?? (async () => {});
}
